/** Saved places: Home, Work and favourites, for one-tap booking. */

import { del, errorMessage, get, post } from "@taxi/mobile-web/api";
import { usePlaceSearch, useResource } from "@taxi/mobile-web/hooks";
import {
  BriefcaseIcon,
  HomeIcon,
  PinIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from "@taxi/mobile-web/icons";
import {
  Button,
  Empty,
  Field,
  IconButton,
  Loading,
  Notice,
  Row,
  Segmented,
  Sheet,
  Spinner,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { useState } from "react";
import brand from "../../../../brand/brand.json";
import { placeSubtitle, placeTitle } from "../lib/draft";
import type { Place, SavedPlace } from "../lib/types";

type Kind = SavedPlace["kind"];

export function PlacesScreen() {
  const places = useResource(() => get<{ data: SavedPlace[] }>("/customer/places"), []);
  const toast = useToast();
  const [adding, setAdding] = useState<Kind | null>(null);
  const saved = places.data?.data ?? [];
  const home = saved.find((p) => p.kind === "home");
  const work = saved.find((p) => p.kind === "work");
  const others = saved.filter((p) => p.kind === "other");

  async function remove(place: SavedPlace) {
    try {
      await del(`/customer/places/${place.id}`);
      await places.reload();
      toast(`${place.label} removed`);
    } catch (error) {
      toast(errorMessage(error), "error");
    }
  }

  const removeButton = (place: SavedPlace) => (
    <IconButton label={`Remove ${place.label}`} onClick={() => void remove(place)}>
      <TrashIcon size={18} />
    </IconButton>
  );

  return (
    <div className="places">
      <TopBar title="Saved places" back="/account" />
      <div className="screen-body">
        {places.loading && !places.data ? (
          <Loading />
        ) : (
          <>
            <div className="row-group">
              <Row
                icon={<HomeIcon />}
                title="Home"
                subtitle={home?.address ?? "Add your home address"}
                right={home ? removeButton(home) : undefined}
                onClick={home ? undefined : () => setAdding("home")}
              />
              <Row
                icon={<BriefcaseIcon />}
                title="Work"
                subtitle={work?.address ?? "Add your work address"}
                right={work ? removeButton(work) : undefined}
                onClick={work ? undefined : () => setAdding("work")}
              />
            </div>
            <h2 className="section-title">Favourites</h2>
            {others.length === 0 ? (
              <Empty icon={<StarIcon size={28} />} title="No favourites yet">
                <p>Save places you go often, like the station or the gym.</p>
              </Empty>
            ) : (
              <div className="row-group">
                {others.map((place) => (
                  <Row
                    key={place.id}
                    icon={<StarIcon />}
                    title={place.label}
                    subtitle={place.address}
                    right={removeButton(place)}
                  />
                ))}
              </div>
            )}
            <Button variant="secondary" onClick={() => setAdding("other")}>
              <PlusIcon size={18} /> Add a place
            </Button>
          </>
        )}
      </div>
      <AddPlace
        kind={adding}
        onClose={() => setAdding(null)}
        onSaved={async () => {
          setAdding(null);
          await places.reload();
          toast("Place saved");
        }}
      />
    </div>
  );
}

function AddPlace({
  kind,
  onClose,
  onSaved,
}: {
  kind: Kind | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [chosenKind, setChosenKind] = useState<Kind>("other");
  const [label, setLabel] = useState("");
  const [query, setQuery] = useState("");
  const [place, setPlace] = useState<Place | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const search = usePlaceSearch(place ? "" : query, brand.map.centre);
  const effectiveKind = kind === "other" ? chosenKind : (kind ?? "other");

  function close() {
    setLabel("");
    setQuery("");
    setPlace(null);
    setError(null);
    setChosenKind("other");
    onClose();
  }

  async function save() {
    if (!place) return;
    setBusy(true);
    setError(null);
    try {
      await post("/customer/places", {
        kind: effectiveKind,
        label:
          effectiveKind === "home"
            ? "Home"
            : effectiveKind === "work"
              ? "Work"
              : label.trim() || placeTitle(place),
        address: place.address,
        postcode: place.postcode,
        lat: place.lat,
        lng: place.lng,
      });
      setLabel("");
      setQuery("");
      setPlace(null);
      await onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const title = kind === "home" ? "Add Home" : kind === "work" ? "Add Work" : "Add a place";
  return (
    <Sheet open={kind !== null} title={title} onClose={close}>
      {kind === "other" && (
        <Segmented
          label="Kind of place"
          value={chosenKind}
          options={[
            { value: "other", label: "Favourite" },
            { value: "home", label: "Home" },
            { value: "work", label: "Work" },
          ]}
          onChange={setChosenKind}
        />
      )}
      {effectiveKind === "other" && (
        <Field
          label="Name"
          placeholder="e.g. Gym, Mum's"
          value={label}
          maxLength={40}
          onChange={(e) => setLabel(e.target.value)}
        />
      )}
      {place ? (
        <Row
          icon={<PinIcon />}
          title={placeTitle(place)}
          subtitle={placeSubtitle(place)}
          right={
            <Button block={false} variant="ghost" onClick={() => setPlace(null)}>
              Change
            </Button>
          }
        />
      ) : (
        <>
          <Field
            label="Address or postcode"
            value={query}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
          />
          <ul className="suggestions compact">
            {search.loading && (
              <li className="suggestions-status">
                <Spinner small /> Searching…
              </li>
            )}
            {search.results.map((result) => (
              <li key={`${result.lat},${result.lng},${result.address}`}>
                <button type="button" className="suggestion" onClick={() => setPlace(result)}>
                  <span className="suggestion-icon">
                    <PinIcon />
                  </span>
                  <span className="suggestion-text">
                    <strong>{placeTitle(result)}</strong>
                    <span>{placeSubtitle(result)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {error && <Notice tone="error">{error}</Notice>}
      <Button loading={busy} disabled={!place} onClick={save}>
        Save
      </Button>
    </Sheet>
  );
}
